/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Tooltip } from "react-tooltip";
import BaseCom from "./BaseCom";
import L from "./Lang";
import './Tutorial.css';
import React from "react";
import { qreq } from "../shared/qrequest";
import { PropertiesEditorGlobal } from "./PropertiesEditor";

class Bubble extends BaseCom {
    render() {
        return (
            <Tooltip isOpen anchorSelect={this.props.anchorSelect} place={this.props.place} style={{ zIndex: 1999, pointerEvents: 'all', borderRadius: '25px', boxShadow: '0px 0px 10px #fff', padding: '15px' }}>
                <div className="fs-5 bubble-content">
                    {this.props.children}
                </div>
                <div className="d-flex gap-1 justify-content-end mt-1">
                    <button type="button" onClick={this.props.onEnd} className="btn text-warning">Skip Tutorial</button>
                    {this.props.currentStep > 0 && <button type="button" onClick={this.props.onPrev} className="btn btn-secondary">Prev</button>}
                    <button type="button" onClick={this.props.onNext} className="btn btn-primary">{this.props.last ? <L>Finish Tutorial</L> : <L>Next</L>}</button>
                </div>
            </Tooltip>
        );
    }
}

export default class Tutorial extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            show: false,
            steps: [
                {
                    anchorSelect: '.tt-nav',
                    place: 'right',
                    content: <>
                        <p><L>Let's get to know the Web Builder options menu: These are simple, yet powerful features that you will be working with located on the left side of the Build Area:</L></p>
                        <p><L>The Tools: drag and drop elements that will be the building blocks of the site;</L></p>
                        <p><L>Design: settings for those elements that you can adjust to bring forth your vision; </L></p>
                        <p><L>Pages: the page creation, deletion, and site-wide reset; </L></p>
                        <p><L>Images: our integrated stock library, and Help: Help options that can be accessed at any time.</L></p>
                        <p><L>Menu: This is where you organize the Website's main menu navigation and internal page links after the navigation element box is placed onto the build area.</L></p>
                    </>
                },
                {
                    anchorSelect: '.tt-tools',
                    place: 'left',
                    content: <>
                        <p><L>Tools are the building blocks of your website. These are draggable column rows, buttons, image spaces, text boxes, text boxes with background color, and the site's navigation menu.</L></p>
                        <p><L>First are the Columns: </L></p>
                        <p><L>All of the sites created with the Web Builder will have rows. These are organized into adjustable columns. The Columns tools are organized by the number of columns. The column sizes in the row can be adjusted by percentages.</L></p>
                        <p><L>Buttons are calls to action that can be linked to internal pages, outside URL, or site functions.</L></p>
                        <p><L>The Image option is to drag and drop an image place-holder that can later be filled with an uploaded image, or with something from our stock library.</L></p>
                        <p><L>Text boxes can be added with or without a background. These include an edit pencil button that will activate the "what you see is what you get" Editor, this is a text edition tool that will work similarly to Microsoft Word.</L></p>
                        <p><L>The Nav Bar, or the Navigation bar placeholder is the space that will hold where the website's menu buttons will be. It can be adjusted with the 'Menu' option located on the left of the work area.</L></p>
                    </>,
                    onCurrent: () => {
                        this.props.setToggle('com', true);
                    },
                    delay: 500
                },
                {
                    anchorSelect: '.tt-design-canvas',
                    place: 'left',
                    content: <>
                        <p><L>The Styling design settings will allow you to control the font color, and the element background; the margins and the padding, as well as the element height or setting a max height.</L></p>
                        <p><L>The element color options are to adjust mostly text area background and font color in a general way. However, if used, the font control within the text editor will override the general setting. This is because the text editor provides more robust text formatting features.</L></p>
                        <p><L>The element background color is also independent of the section background color or image, more on this control in a later tutorial.</L></p>
                        <p><L>The Margins is the spacing around an element and the Padding is the spacing on the inside of a chosen element. </L></p>
                        <p><L>The Height and Max Height options are for setting limits of an element within the assigned column. This is for design or aesthetic reasons and can be left to resize with the column itself as a default. In lamens terms: You don't need to touch this option if it isn't necessary</L></p>
                    </>,
                    onCurrent: () => {
                        this.props.setToggle('design', true);
                    },
                    delay: 500
                },
                {
                    anchorSelect: '.tt-design-font',
                    place: 'left',
                    content: <>
                        <p><L>The Font is the general setting for the fonts in a textbox. You can change from the default font to a web-friendly font, you can change the font size, the font color, and the font's weight: this is how bold a font will look. Letter spacing is the space between the letters and the line height is the distance between lines of text.</L></p>
                        <p><L>Again, these are general settings, outside of the text editor for the text box. If you change the settings within the text editor window, these settings will override the general settings.</L></p>
                        <p><L>This is set up the way it is for design reasons. If, within the design, different text boxes need to have different font settings, they can have those different settings.</L></p>
                    </>,
                    onCurrent: () => {
                        PropertiesEditorGlobal.setAccordionActiveKey('3');
                    },
                    delay: 500
                },
                {
                    anchorSelect: '.tt-pages',
                    place: 'right',
                    content: <L>Use the "Pages" menu to add new pages to the website.</L>
                },
                {
                    anchorSelect: '.tt-save',
                    place: 'bottom',
                    content: <L>Click here to save your changes. Do this before you preview.</L>,
                    onCurrent: () => {
                        this.props.setToggle(null, false);
                    }
                },
                {
                    anchorSelect: '.tt-preview',
                    place: 'bottom',
                    content: <L>Click here to preview your website. Your website will not be live until published.</L>
                },
                {
                    anchorSelect: '.tt-purchase',
                    place: 'bottom',
                    content: <L>Once you are ready, click here to purchase the website. Your website will be live and visible to everyone once purchased.</L>
                }
            ],
            anchorSelect: '.tt-com',
            currentStep: 0
        };
        this.bgTop = React.createRef();
        this.bgLeft = React.createRef();
        this.bgRight = React.createRef();
        this.bgBottom = React.createRef();
    }

    async componentDidMount() {
        this.highlight();

        try
        {
            let j = await qreq.getAsync('/api/tutorial/status');
            if (j.errorCode === 0 && !j.item)
                this.setState({ show: true });
        }
        catch
        {}
    }

    getStep = () => {
        return this.state.steps[this.state.currentStep];
    }

    highlight = () => {
        setTimeout(() => {
            if (!this.getStep()) return;

            let current = this.getStep();
            if (current.onCurrent)
                current.onCurrent();

            setTimeout(() => {
                let el = document.querySelector(this.getStep().anchorSelect);
                if (el) {
                    let rect = el.getBoundingClientRect();
                    if (!this.bgTop.current || !this.bgLeft.current || !this.bgRight.current || !this.bgBottom.current)
                        return;
                    this.bgTop.current.style.height = rect.top + 'px';
                    this.bgTop.current.style.width = '100%';
                    this.bgTop.current.style.left = '0';
                    this.bgTop.current.style.top = '0';

                    this.bgLeft.current.style.height = (rect.bottom - rect.top) + 'px';
                    this.bgLeft.current.style.width = rect.left + 'px';
                    this.bgLeft.current.style.left = '0';
                    this.bgLeft.current.style.top = rect.top + 'px';

                    this.bgRight.current.style.height = (rect.bottom - rect.top) + 'px';
                    this.bgRight.current.style.width = window.innerWidth - rect.right + 'px';
                    this.bgRight.current.style.left = rect.right + 'px';
                    this.bgRight.current.style.top = rect.top + 'px';

                    this.bgBottom.current.style.height = window.innerHeight - rect.bottom + 'px';
                    this.bgBottom.current.style.width = '100%';
                    this.bgBottom.current.style.left = '0';
                    this.bgBottom.current.style.top = rect.bottom + 'px';
                }
                this.setState({ anchorSelect: this.getStep().anchorSelect });
            }, this.getStep().delay || 0);


        }, 100);
    }

    next = () => {
        if (this.state.currentStep >= this.state.steps.length - 1) {
            this.end();
            return;
        }
        this.setState({ currentStep: this.state.currentStep + 1 });
        this.highlight();
    }

    prev = () => {
        if (this.state.currentStep === 0) return;
        this.setState({ currentStep: this.state.currentStep - 1 });
        this.highlight();
    }

    end = () => {
        this.setState({ show: false });
        qreq.post('/api/tutorial/done', {}, j => {});
    }

    render() {
        if (!this.state.show) return null;
        return (<>
            <div ref={this.bgTop} className="tutorial-bg"></div>
            <div ref={this.bgLeft} className="tutorial-bg"></div>
            <div ref={this.bgRight} className="tutorial-bg"></div>
            <div ref={this.bgBottom} className="tutorial-bg"></div>
            <Bubble currentStep={this.state.currentStep} last={this.state.currentStep === this.state.steps.length - 1} anchorSelect={this.getStep()?.anchorSelect} place={this.getStep()?.place} onNext={this.next} onPrev={this.prev} onEnd={this.end}>
                {this.getStep()?.content}
            </Bubble>
        </>
        );
    }
}